import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Empty, Spin } from 'antd'
import Markdown from 'markdown-to-jsx'
import { useHistory } from 'react-router-dom'
import { getAicDashboardDatasetDetail } from '../../request'
import ApplicationOverview from './ApplicationOverview'
import OperationsPanel from './operations/OperationsPanel'
import {
  EMPTY,
  deriveLedgerStatusKey,
  formatBytes,
  formatDateTime,
  formatInteger,
  formatMdSummary,
  formatPlan,
  formatTimeRange,
  formatUpdatedAt,
  formatVolume,
  healthLabel,
  ledgerStatusMeta,
  termDisplay
} from './terminalModel'

// P6：AIC 数据集详情保留 4 个 tab；Vibe 应用项目只有「概览 + 操作」。
const DATASET_TABS = [
  ['overview', '概览'],
  ['structure', '表结构'],
  ['dictionary', '数据字典'],
  ['operations', '操作']
]
const APPLICATION_TABS = [
  ['overview', '概览'],
  ['operations', '操作']
]
const tabsOf = projectKind => projectKind === 'application' ? APPLICATION_TABS : DATASET_TABS

const legacyDetailPath = projectId => `/portal/aic-dashboard/datasets/${encodeURIComponent(projectId)}`

const sensitivityText = level => ({
  L1: 'L1 · 全员可见',
  L2: 'L2 · 部门可见',
  L3: 'L3 · 授权可见'
}[level] || termDisplay(level))

function OverviewPanel ({ row, detail }) {
  const history = useHistory()
  const meta = row ? ledgerStatusMeta(deriveLedgerStatusKey(row)) : null
  const ownerText = [termDisplay(row && row.owner), sensitivityText(row && row.sensitivityLevel)].join(' · ')
  return <div className="term-panel">
    <dl className="term-kv">
      <dt>状态</dt>
      <dd>
        {meta
          ? <span className="term-state"><span className={`term-dot term-dot-${meta.key}`} />{meta.label}</span>
          : <span className="term-muted">{termDisplay(row && row.healthStatus ? healthLabel(row.healthStatus) : null)}</span>}
      </dd>
      <dt>数据集 ID</dt><dd>{termDisplay(detail && detail.datasetId)}</dd>
      <dt>项目 ID</dt><dd>{termDisplay((detail && detail.projectId) || (row && row.projectId))}</dd>
      <dt>Owner / 权限</dt><dd>{ownerText}</dd>
      <dt>数据量</dt><dd>{formatVolume(row)}</dd>
      <dt>大小</dt><dd>{formatBytes(row && row.attachmentBytes)}</dd>
      <dt>数据字典</dt><dd>{formatMdSummary(row && row.dictionary)}{detail && detail.dataDictionarySyncedAt ? ` · 同步 ${formatDateTime(detail.dataDictionarySyncedAt)}` : ''}</dd>
      <dt>更新计划</dt><dd>{formatPlan(row && row.updatePlanText, row && row.nextExpectedUploadAt)}</dd>
      <dt>时间范围</dt><dd>{formatTimeRange(row && row.dataDateRange)}</dd>
      <dt>最近更新</dt><dd>{formatUpdatedAt(row && row.lastSuccessfulUploadAt)}</dd>
      <dt>说明</dt>
      <dd>{termDisplay((row && row.anomalyReason) || (detail && detail.healthReason) || (detail && detail.description))}</dd>
    </dl>
    <div className="term-panel-actions">
      <button
        type="button"
        className="term-link"
        onClick={() => history.push(legacyDetailPath((row && row.projectId) || (detail && detail.projectId)))}
      >
        打开旧版详情
      </button>
      <span className="term-muted">数据导入与授权请使用「操作」tab；写操作会按权限二次校验。</span>
    </div>
  </div>
}

function StructurePanel ({ detail }) {
  const tables = detail && Array.isArray(detail.tables) ? detail.tables : []
  const fields = detail && Array.isArray(detail.fields) ? detail.fields : []
  const fieldCountByTable = useMemo(() => fields.reduce((result, field) => {
    const tableId = field && field.tableId
    if (tableId === null || tableId === undefined) return result
    result[tableId] = (result[tableId] || 0) + 1
    return result
  }, {}), [fields])
  if (tables.length === 0) {
    return <div className="term-panel">
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="没有已发布的逻辑表" />
    </div>
  }
  return <div className="term-panel">
    <div className="term-panel-title">表结构（{tables.length} 张）</div>
    <table className="term-table term-table-plain">
      <thead>
        <tr>
          <th>表 / 逻辑名</th>
          <th className="term-num">行数</th>
          <th className="term-num">字段数</th>
        </tr>
      </thead>
      <tbody>
        {tables.map(table => {
          const key = table.tableId === null || table.tableId === undefined ? table.logicalName : table.tableId
          const label = table.displayName && table.displayName !== table.logicalName
            ? `${table.displayName}（${table.logicalName}）`
            : (table.logicalName || table.displayName)
          return <tr key={key}>
            <td className="term-mono" title={termDisplay(label)}>{termDisplay(label)}</td>
            <td className="term-num">{formatInteger(table.publishedRowCount)}</td>
            <td className="term-num">{fieldCountByTable[table.tableId] === undefined ? EMPTY : formatInteger(fieldCountByTable[table.tableId])}</td>
          </tr>
        })}
      </tbody>
    </table>
    <div className="term-foot">数据来源：AIC 可见目录 + 只读元数据统计；不含任何连接凭据。</div>
  </div>
}

function DictionaryPanel ({ detail }) {
  const markdown = detail && detail.dataDictionaryMd ? String(detail.dataDictionaryMd) : ''
  if (!markdown) {
    return <div className="term-panel">
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据字典，可使用 AIC Skill 生成并上传" />
    </div>
  }
  return <div className="term-panel">
    <div className="term-panel-title">数据字典（{formatInteger(markdown.length)} 字符 · 同步 {formatDateTime(detail.dataDictionarySyncedAt)}）</div>
    <div className="term-md">
      <Markdown options={{ disableParsingRawHTML: true }}>{markdown}</Markdown>
    </div>
  </div>
}

/**
 * 行下钻 overlay：AIC 数据集为概览/表结构/数据字典/操作；Vibe 应用项目只有概览/操作。
 * 数据字典为 longtext，只在这里按需拉取；Markdown 关闭原始 HTML 解析，避免注入。
 * 「操作」tab 的授权/申请/审批成功后会通过 onUpdated 重新拉一次 detail（权限标记、data_owner 等）。
 * 物理删除成功后：先调 onDeleted（上层刷新台账），再由「维护操作」分区调 onClose 关闭本浮层。
 */
export default function TerminalDetail ({ row, initialTab = 'overview', onClose, onDeleted }) {
  const [tab, setTab] = useState(initialTab)
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const mountedRef = useRef(true)
  const projectId = row ? row.projectId : null

  const loadDetail = useCallback(() => {
    if (projectId === null || projectId === undefined || projectId === '') {
      setLoading(false)
      return Promise.resolve()
    }
    setLoading(true)
    setError('')
    return getAicDashboardDatasetDetail(projectId)
      .then(value => { if (mountedRef.current) setDetail(value && value.projectId ? value : null) })
      .catch(() => { if (mountedRef.current) setError('数据单元详情加载失败') })
      .finally(() => { if (mountedRef.current) setLoading(false) })
  }, [projectId])

  useEffect(() => {
    mountedRef.current = true
    setTab(initialTab)
    setDetail(null)
    loadDetail()
    return () => { mountedRef.current = false }
  }, [initialTab, loadDetail])

  // 行数据里已有 projectKind（台账行）时先按它决定 tab，详情返回后再以后端字段为准。
  const projectKind = (detail && detail.projectKind) || (row && row.projectKind) || 'dataset'
  const tabs = tabsOf(projectKind)
  // 外部传入的 initialTab 对当前类型不可用时（如应用项目带 dictionary）回落到概览。
  const activeTab = tabs.some(([key]) => key === tab) ? tab : 'overview'

  return <div className="term-overlay" role="dialog" aria-modal="true" aria-label="数据单元详情">
    <div className="term-top term-top-overlay">
      <button type="button" className="term-back" onClick={onClose}>← 返回</button>
      <div className="term-h1">{termDisplay(row && row.unitName)}</div>
      <div className="term-top-meta">
        <span>{termDisplay(row && row.owner)}</span>
        <span>{termDisplay(row && row.department)}</span>
      </div>
    </div>
    <div className="term-overlay-tabs">
      {tabs.map(([key, label]) => <button
        key={key}
        type="button"
        className={`term-mtab ${activeTab === key ? 'on' : ''}`}
        aria-current={activeTab === key ? 'page' : undefined}
        onClick={() => setTab(key)}
      >
        {label}
      </button>)}
    </div>
    <div className="term-overlay-body">
      <Spin spinning={loading}>
        {error && <div className="term-notice term-notice-error">{error}</div>}
        {!loading && !error && !detail && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="数据单元不存在或无法读取" />}
        {detail && activeTab === 'overview' && (projectKind === 'application'
          ? <ApplicationOverview row={row} detail={detail} />
          : <OverviewPanel row={row} detail={detail} />)}
        {detail && activeTab === 'structure' && <StructurePanel detail={detail} />}
        {detail && activeTab === 'dictionary' && <DictionaryPanel detail={detail} />}
        {detail && activeTab === 'operations' && <OperationsPanel
          row={row}
          detail={detail}
          projectKind={projectKind}
          onUpdated={loadDetail}
          onClose={onClose}
          onDeleted={onDeleted}
        />}
      </Spin>
    </div>
  </div>
}
